import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, shareReplay, tap, throwError } from 'rxjs';
import { HisAuthService } from '../../auth/service/his-auth.service';
import { GenericValue } from '../../core/model/generic-value';
import { HisBackendRequestOptions, HisBackendService } from '../../core/service/backend/his-backend.service';
import { HisGenericValueService } from '../../core/service/base/his-generic-value.service';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { ConversationRecordEditData } from '../model/communication-log-edit-data.model';
import { CommunicationLogEntry } from '../model/communication-log-entry.model';
import { CommunicationLogParticipant } from '../model/communication-log-participant.model';
import { CommunicationLogFilter } from '../model/communication-log-filter';

type Entry = {
    id: number,
    senderId: number,
    senderName: string,
    recipientId: number,
    recipientName: string,
    title: string,
    type: string,
    subtype: string,
    domainIds: number[],
    createdAt: number,
    editable: boolean,
    deletable: boolean,
    additionalAttributes: any
}

type ServerResponse = {
    ownerName: string,
    entries: Entry[]
}

export function byTimeNewestFirst( a: CommunicationLogEntry, b: CommunicationLogEntry ): number {
    return b?.createdAt.getTime() - a?.createdAt.getTime();
}

@Injectable({providedIn: 'root'})
export class HisCommunicationlogService {
    private backendService = inject(HisBackendService);
    private genericValueService = inject(HisGenericValueService);
    private uiService = inject(HisUiService);
    private navigationService = inject(HisNavigationService);
    private auth = inject(HisAuthService);

    private readonly apiBase = 'api/v1/cs/sys/communicationlog';

    private readonly allowedDomainIds$: Observable<number[]> = this.backendService.getData( `${this.apiBase}/alloweddomains` ).pipe( shareReplay( 1 ) );
    private readonly allDomainsCached$: Observable<GenericValue[]> = this.genericValueService.getValueTypes( 'domainValue' ).pipe(
        map( domains => {
            return domains.sort( ( a, b ) => {
                return a.defaulttext?.localeCompare( b.defaulttext ) ?? a.uniquename.localeCompare( b.uniquename );
            } );
        } ), shareReplay( 1 ) );

    private readonly allowedDomains$: Observable<GenericValue[]> = forkJoin( {
        allowedIds: this.allowedDomainIds$,
        domains: this.allDomainsCached$
    } ).pipe( map( result => {
        return result.domains.filter( domain => result.allowedIds.includes( domain.id ) );
    } ), shareReplay( 1 ) );

    private readonly pageSize$: Observable<number> = this.backendService.getData( `${this.apiBase}/config/pagesize` ).pipe( shareReplay( 1 ) );

    private personCache: Map<number, CommunicationLogParticipant> = new Map();

    public findByOwnerId( id: number, authorizationContext: string, olderThan = new Date( '2100-12-31' ) ): Observable<CommunicationLogEntry[]> {
        const requestOptions: HisBackendRequestOptions = { showPermissionError: false };
        let restParams = new HttpParams().set( 'olderThan', olderThan?.toISOString() );
        if ( authorizationContext ) {
            restParams = restParams.set( 'authorizationContext', authorizationContext );
        }

        const apiUrl = `${this.apiBase}/${id}?${restParams.toString()}`;
        return this.backendService.getData( apiUrl, requestOptions ).pipe(
            catchError( error => {
                if ( error.status === 401 || error.status === 403 ) {
                    this.navigationService.navigateToAccessDenied();
                }
                return throwError( () => error );
            } ),
            tap( ( response: ServerResponse ) => this.personCache.set( id, new CommunicationLogParticipant( id, response.ownerName ) ) ),
            map( ( response ) => response.entries ),
            map( ( rawEntries: Entry[] ) => rawEntries.map( ( entry ) => this.convertToModel( entry, id ) ) ),
            map( ( entries ) => entries.filter( Boolean ) ),
            map( ( entries ) => entries.sort( byTimeNewestFirst ) ),
            tap( ( entries ) => entries.forEach( entry => this.addToPersonCacheIfNotExists( entry ) ) ) );
    }

    private addToPersonCacheIfNotExists( entry: CommunicationLogEntry ): void {
        if ( !this.personCache.has( entry.senderId ) ) {
            this.personCache.set( entry.senderId, new CommunicationLogParticipant( entry.senderId, entry.senderName ) );
        }
        if ( !this.personCache.has( entry.recipientId ) ) {
            this.personCache.set( entry.recipientId, new CommunicationLogParticipant( entry.recipientId, entry.recipientName ) );
        }
    }

    private getNameOrTranslation( name: string ): string {
        if ( name?.includes( '.' ) ) {
            return this.uiService.translate( name );
        }
        return name;
    }

    protected isOutgoing( entry: Entry, ownerId: number ): boolean {
        if ( ownerId === this.auth.personId ) {
            return entry.senderId === ownerId;
        }
        return entry.senderId !== ownerId;
    }

    private convertToModel( raw: Entry, ownerId: number ): CommunicationLogEntry {
        const id = raw.id;
        const title = raw.title;
        const createdAt = new Date( raw.createdAt );
        const domainIds = raw.domainIds;
        const senderId = raw.senderId;
        const senderName = this.getNameOrTranslation( raw.senderName );
        const recipientId = raw.recipientId;
        const recipientName = this.getNameOrTranslation( raw.recipientName );
        const type = raw.type;
        const subtype = raw.subtype;
        const editable = raw.editable;
        const deletable = raw.deletable;
        const incomming = !this.isOutgoing( raw, ownerId );
        const additionalAttributes = raw.additionalAttributes;

        return new CommunicationLogEntry( id, title, createdAt, domainIds, senderId, senderName, recipientId, recipientName, type, subtype, editable, deletable, incomming, additionalAttributes );
    }

    public createConversationRecord( ownerId: number, conversationRecord: ConversationRecordEditData, authorizationContext?: string ): Observable<number> {
        let url = `${this.apiBase}/${ownerId}/createconversationrecord`;
        if ( authorizationContext ) {
            const params = new HttpParams().set( 'authorizationContext', authorizationContext );
            url = `${url}?${params.toString()}`;
        }
        return this.backendService.postData( url, conversationRecord );
    }

    public updateConversationRecord( conversationRecord: ConversationRecordEditData ): Observable<void> {
        return this.backendService.putData( `${this.apiBase}/update`, conversationRecord );
    }

    public getAllDomains( logOwnerId: number ): Observable<GenericValue[]> {
        if ( this.auth.personId === logOwnerId ) {
            return this.allDomainsCached$;
        }
        return this.allowedDomains$;
    }

    public deleteEntry( id: number ): Observable<void> {
        return this.backendService.delete( `${this.apiBase}/delete/${id}` );
    }

    public getParticipantName( id: number ): CommunicationLogParticipant {
        if ( id && this.personCache.has( id ) ) {
            return this.personCache.get( id );
        }

        return new CommunicationLogParticipant( id, '' );
    }

    public getPageSize(): Observable<number> {
        return this.pageSize$;
    }

    public getFilterDefinitions(): Observable<CommunicationLogFilter[]> {
        return this.backendService.getData( `${this.apiBase}/filterdefinitions` );
    }
}
